import { ProvetDepartment } from "../types";

/**
 * Applies the CMS settings to the departments fetched from the API.
 *
 * - The "Active Provet departments" list is the single source of truth for both the
 *   clinic list and the map: a clinic on the list books in this form (`isActive` true),
 *   a clinic not on it links to the old booking. An empty list means no clinic books
 *   in this form.
 * - Clinics on the "Excluded Provet departments" list are left out completely.
 *
 * Returns new department objects; the input is not changed.
 */
export function applyDepartmentSettings(
  departments: ProvetDepartment[],
  activeDepartmentIds: number[],
  excludedDepartmentIds: number[]
): ProvetDepartment[] {
  return departments
    .filter(department => !excludedDepartmentIds.includes(department.id ?? 0))
    .map(department => ({
      ...department,
      isActive: activeDepartmentIds.includes(department.id ?? 0)
    }));
}

let hasWarnedAboutMissingDomain = false;

/**
 * Builds the link to the old Provet booking for a clinic that doesn't book in this form,
 * in the page language (the same link format the clinic page uses).
 *
 * Returns undefined (and warns once) when the old booking domain (`Provet:baseUrl`) isn't
 * configured, so callers don't render a relative link that leads nowhere.
 */
export function getOldBookingUrl(
  oldBookingDomain: string | undefined,
  departmentId: number | undefined,
  language: string | undefined
): string | undefined {
  if (!oldBookingDomain?.trim()) {
    if (!hasWarnedAboutMissingDomain) {
      hasWarnedAboutMissingDomain = true;
      console.warn(
        "Provet: no old booking domain (Provet:baseUrl) is configured, so clinics booked on a separate page get no link."
      );
    }

    return undefined;
  }

  const query = new URLSearchParams({
    department: String(departmentId ?? 0),
    lang: language || "sv"
  });

  return `${oldBookingDomain}/onlinebooking/?${query.toString()}`;
}

/**
 * Filters departments by a free-text search on name, city, street address or zip code
 * (case-insensitive). An empty search returns every department.
 */
export function filterDepartmentsBySearch(
  departments: ProvetDepartment[],
  searchTerm: string
): ProvetDepartment[] {
  const searchLower = searchTerm.trim().toLowerCase();

  if (!searchLower) {
    return departments;
  }

  return departments.filter(department =>
    [
      department.name,
      department.address_info?.city,
      department.address_info?.street_address_1,
      department.address_info?.zip_code
    ].some(value => value?.toLowerCase().includes(searchLower))
  );
}
